import type { SelectedOptions } from 'src/components/filter-dropdown';

import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useMemo, useState, useCallback } from 'react';

import { Stack } from '@mui/material';

import { useListForFilter } from 'src/hooks/use-list-for-filter';

import { endpoints } from 'src/utils/axios';

import { useGetAuditTrailEventTypes, useGetAuditTrailEntityTypes } from 'src/actions/audit-trails';

import { Filters } from 'src/components/filters';
import { CustomDateRangePicker } from 'src/components/custom-date-range-picker';

import { useAuthContext } from 'src/auth/hooks';

interface Props {
  onFiltersChange: (filters: Record<string, string[]>) => void;
  onSearchChange: (search: string) => void;
  onDateRangeChange: (dateRange: { startDate: string; endDate: string } | null) => void;
  hideEntityTypesFilter?: boolean;
}

export function AuditTrailFilters({
  onFiltersChange,
  onSearchChange,
  onDateRangeChange,
  hideEntityTypesFilter = false,
}: Props) {
  const { t } = useTranslation('organization');
  const { user } = useAuthContext();
  const [openCalendar, setOpenCalendar] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<SelectedOptions[]>([]);
  const [selectedDateRange, setSelectedDateRange] = useState<{
    startDate?: dayjs.Dayjs;
    endDate?: dayjs.Dayjs;
  } | null>(null);
  const {
    paginatedData: usersData,
    onLoadMore: onLoadMoreUsers,
    onSearchChange: onSearchChangeUsers,
  } = useListForFilter<{ id: number | string; name: string }>({
    endpoint: endpoints.clients.users.list(user?.client?.id ?? 0),  
    additionalParams: {
      'select-fields': 'id,name',
    },
  });

  const { entityTypes } = useGetAuditTrailEntityTypes();
  const { eventTypes } = useGetAuditTrailEventTypes();

  const filterOptions = useMemo(
    () => ({
      userIds: {
        label: t('audit-trails.filters.users'),
        children: usersData?.map((userData) => ({ label: userData.name, key: userData.id })) || [],
        onSearch: onSearchChangeUsers,
        loadMore: onLoadMoreUsers,
      },
      ...(!hideEntityTypesFilter
        ? {
            entityTypes: {
              label: t('audit-trails.filters.entityTypes'),
              children:
                entityTypes?.map((entityType) => ({
                  label: t(`audit-trails.entityLinks.${entityType}`),
                  key: entityType,
                })) || [],
            },
          }
        : {}),
      actionTypes: {
        label: t('audit-trails.filters.eventTypes'),
        children:
          eventTypes?.map((eventType) => ({
            label: t(`audit-trails.events.${eventType}`),
            key: eventType,
          })) || [],
      },
      dateRange: {
        label: t('audit-trails.filters.dateRange'),
        onClick: () => {
          setOpenCalendar(true);
        },
        maxWidth: 250,
      },
    }),
    [
      usersData,
      t,
      entityTypes,
      eventTypes,
      hideEntityTypesFilter,
      onLoadMoreUsers,
      onSearchChangeUsers,
    ]
  );

  const onChange = useCallback(
    (newFilters: SelectedOptions[]) => {
      setSelectedOptions(newFilters);
      onFiltersChange({
        ...newFilters.reduce(
          (acc: any, filter) => ({
            ...acc,
            [filter.field]: [...(acc[filter.field] ?? []), filter.key],
          }),
          {}
        ),
      });
      if (!newFilters.find((filter) => filter.field === 'dateRange')) {
        setSelectedDateRange(null);
        onDateRangeChange(null);
      }
    },
    [onFiltersChange, onDateRangeChange]
  );

  const onDateRangeApply = useCallback(
    (startDate: dayjs.Dayjs | null, endDate: dayjs.Dayjs | null) => {
      if (startDate && endDate) {
        setSelectedOptions([
          ...selectedOptions.filter((option) => option.field !== 'dateRange'),
          {
            field: 'dateRange',
            key: 'selected_date_range',
            label: `${startDate.format('DD MMM YY')} - ${endDate.format('DD MMM YY')}`,
          },
        ]);
        onDateRangeChange({
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        });
      } else {
        setSelectedDateRange(null);
      }

      onFiltersChange({
        ...selectedOptions.reduce(
          (acc: any, filter) => ({
            ...acc,
            [filter.field]: [...(acc[filter.field] ?? []), filter.key],
          }),
          {}
        ),
      });
    },
    [onFiltersChange, onDateRangeChange, selectedOptions]
  );
  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
        <Filters
          selectedOptions={selectedOptions}
          options={filterOptions}
          enableSearch
          onFiltersChange={onChange}
          onValueChange={onSearchChange}
        />
      </Stack>
      <CustomDateRangePicker
        open={openCalendar}
        variant="calendar"
        onClose={() => setOpenCalendar(false)}
        onApply={() => {
          setOpenCalendar(false);
          onDateRangeApply(
            selectedDateRange?.startDate ?? dayjs(),
            selectedDateRange?.endDate ?? dayjs()
          );
        }}
        startDate={selectedDateRange?.startDate ?? dayjs()}
        endDate={selectedDateRange?.endDate ?? dayjs()}
        onChangeStartDate={(date) =>
          setSelectedDateRange({ ...selectedDateRange, startDate: dayjs(date) })
        }
        onChangeEndDate={(date) =>
          setSelectedDateRange({ ...selectedDateRange, endDate: dayjs(date) })
        }
      />
    </>
  );
}
